import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { toast } from '@/components/ui/use-toast';
import AgentCard from '@/components/crew/AgentCard';
import SearchFilter from '@/components/crew/SearchFilter';
import AgentDetailModal from '@/components/crew/AgentDetailModal';
import CrewHeader from '@/components/crew/CrewHeader';
import { CrewAgent } from '@/types/crew-types';
import { crewAgents, allCategories } from '@/data/crew-agents-ordered';
import { trackActivity } from '@/utils/tracking';
import { useSite } from '@/contexts/SiteContext';

const CrewPage = () => {
  const { selectedSite } = useSite();
  const [selectedAgent, setSelectedAgent] = useState<CrewAgent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleAgents, setVisibleAgents] = useState<Set<string>>(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const agentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const filteredAgents = useMemo(() => {
    return crewAgents.filter((agent) => {
      // Category filter
      const categoryMatch = selectedCategory === 'All' || agent.category.includes(selectedCategory as any);
      
      // Search filter
      const searchMatch = 
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        agent.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.details.role.toLowerCase().includes(searchQuery.toLowerCase());
        
      return categoryMatch && searchMatch;
    });
  }, [selectedCategory, searchQuery]);

  const handleAgentClick = (agent: CrewAgent) => {
    setSelectedAgent(agent);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };
  
  // Track meet_crew when component mounts
  useEffect(() => {
    if (selectedSite) {
      trackActivity('meet_crew', selectedSite._id || selectedSite.id);
    }
  }, [selectedSite]);

  // Setup Intersection Observer for viewport detection
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const agentId = entry.target.getAttribute('data-agent-id');
          if (agentId) {
            setVisibleAgents((prev) => {
              const newSet = new Set(prev);
              if (entry.isIntersecting) {
                newSet.add(agentId);
              } else {
                newSet.delete(agentId);
              }
              return newSet;
            });
          }
        });
      },
      {
        root: null,
        rootMargin: '50px', // Start loading 50px before element is visible
        threshold: 0.01
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Observe agent cards
  const setAgentRef = useCallback((agentId: string, element: HTMLDivElement | null) => {
    if (element) {
      agentRefs.current.set(agentId, element);
      element.setAttribute('data-agent-id', agentId);
      if (observerRef.current) {
        observerRef.current.observe(element);
      } else {
      }
    } else {
      const existingElement = agentRefs.current.get(agentId);
      if (existingElement) {
        observerRef.current?.unobserve(existingElement);
        agentRefs.current.delete(agentId);
      }
    }
  }, []);
  
  // After components mount, trigger an observation check
  useEffect(() => {
    if (!hasInitialized && agentRefs.current.size > 0) {
      setTimeout(() => {
        agentRefs.current.forEach((element, agentId) => {
          if (observerRef.current) {
            // Unobserve and re-observe to trigger intersection check
            observerRef.current.unobserve(element);
            observerRef.current.observe(element);
          }
        });
        setHasInitialized(true);
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [filteredAgents.length, hasInitialized]);

  // Show welcome toast on initial load
  useEffect(() => {
    toast({
      title: "Welcome to PageMind Crew",
      description: "Explore your AI crew and discover their capabilities.",
    });
  }, []);

  return (
    <div className="container mx-auto py-6 px-4">
      <CrewHeader />
      
      <SearchFilter 
        categories={allCategories}
        onCategoryChange={handleCategoryChange}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
      />
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredAgents.length > 0 ? (
          filteredAgents.map((agent) => (
            <div key={agent.id} ref={(el) => setAgentRef(agent.id, el)}>
              <AgentCard 
                agent={agent} 
                onClick={handleAgentClick}
                isInViewport={visibleAgents.has(agent.id)}
              />
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <h3 className="text-xl font-medium">No agents found</h3>
            <p className="text-muted-foreground mt-2">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
      
      <AgentDetailModal 
        agent={selectedAgent} 
        isOpen={isModalOpen} 
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default CrewPage;